import React, { useRef } from 'react';
import { CoverThumbnail } from '@/components/cover/CoverThumbnail';
import { COVER_DEFAULT_VISIBLE_FIELDS } from '@/components/cover/resolve-cover-fields';
import { COVER_PICKER_KEYS } from '@/components/cover/templates/registry';
import { FONT_OPTIONS } from '@/components/customize/constants';
import { ArrowLeft, CloseCircle, InfoCircle, Refresh, Spinner, Trash } from '@/components/icons';
import { SubscriptionPlansModal } from '@/components/modals/SubscriptionPlansModal';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useModalContext } from '@/components/ui/modal-provider';
import { Progress } from '@/components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import ColorSwatchPicker from '@/components/wizard/chart-config/ColorSwatchPicker';
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from '@/constants';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { cn } from '@/lib/utils';
import { IMAGE_UPLOAD_ACCEPT } from '@/utils/validate-image-file';
import { useCoverViewState } from './use-cover-view-state';
import type { CoverFieldKey, CoverTitleSize } from '@/types/cover';

/** Sentinel do Select: radix não aceita item com value vazio. */
const DEFAULT_FONT_VALUE = '__default__';

const TITLE_SIZES: Array<{ value: CoverTitleSize; label: string; title: string }> = [
    { value: 'small', label: 'P', title: 'Pequeno' },
    { value: 'medium', label: 'M', title: 'Médio' },
    { value: 'large', label: 'G', title: 'Grande' },
];

/**
 * A capa é uma página própria: header slim com back button, sem o chrome do laudo.
 * As ações ficam à direita do header (mesmo recorte do header do laudo) para não
 * sumirem no scroll da coluna de controles.
 */
const CoverShell: React.FC<{
    onBack: () => void;
    patientName?: string;
    actions?: React.ReactNode;
    children: React.ReactNode;
}> = ({ onBack, patientName, actions, children }) => (
    <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-border bg-card px-3 py-2 md:flex-nowrap md:px-6 md:py-3">
            <div className="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    data-testid="cover-back"
                    onClick={onBack}
                    aria-label="Voltar ao laudo"
                    className="size-9 flex-shrink-0 rounded-lg border border-border hover:bg-surface-muted"
                >
                    <ArrowLeft className="size-4" />
                </Button>
                <div className="min-w-0">
                    <h1 className="truncate text-sm font-semibold text-foreground sm:text-base">
                        Capa do laudo
                    </h1>
                    {patientName ? (
                        <p className="truncate text-xs text-muted-foreground">{patientName}</p>
                    ) : null}
                </div>
            </div>
            {actions ? (
                <div className="order-3 flex w-full flex-nowrap items-center justify-end gap-1.5 overflow-x-auto pb-1 md:order-none md:w-auto md:gap-3 md:overflow-visible md:pb-0">
                    {actions}
                </div>
            ) : null}
        </div>
        {children}
    </div>
);

/** Botão sólido do header, no mesmo recorte do CTA do laudo. */
const HEADER_PRIMARY_CLASS = 'h-9 flex-shrink-0 rounded-lg px-3 text-xs md:h-10 md:px-4 md:text-sm';
const HEADER_SECONDARY_CLASS =
    'h-9 flex-shrink-0 rounded-lg border-border px-3 text-xs hover:bg-surface-muted md:h-10 md:px-4 md:text-sm';

/** Rótulo longo no desktop, curto quando o header aperta. */
const ResponsiveLabel: React.FC<{ long: string; short: string }> = ({ long, short }) => (
    <>
        <span className="hidden min-[1025px]:inline">{long}</span>
        <span className="min-[1025px]:hidden">{short}</span>
    </>
);

/**
 * The Capa tab: a view switch inside /report (?view=capa). Picker while the
 * laudo has no cover; lean editor afterwards. The laudo preview is never
 * touched — the cover renders only here and in the exported DOCX.
 */
export const CoverView: React.FC = () => {
    const state = useCoverViewState();
    const logoInputRef = useRef<HTMLInputElement>(null);
    const { openModal, closeModal } = useModalContext();
    const confirmDelete = useConfirmDelete();
    const { cover, coverAccess, customizations, fieldContext, isLocked, patientName, templates } =
        state;

    const handleRemoveLogo = () => {
        confirmDelete({
            title: 'Remover a logo?',
            description: 'Ela sai da capa e também do cabeçalho e do rodapé do laudo.',
            confirmText: 'Remover',
            onConfirm: (close) => {
                state.removeLogo();
                close();
            },
        });
    };

    /** Sem a capa no plano, os botões viram o convite ao upgrade. */
    const openPlansModal = () => {
        openModal({
            title: 'Atualize seu Plano',
            content: <SubscriptionPlansModal close={closeModal} />,
            size: 'lg',
        });
    };

    if (!cover) {
        return (
            <CoverShell onBack={state.closeCoverView} patientName={patientName}>
                <div
                    data-testid="cover-view-picker"
                    className="flex min-h-0 flex-1 flex-col items-center gap-6 overflow-auto bg-surface-muted p-8"
                >
                    <div className="text-center">
                        <h2 className="text-lg font-semibold tracking-tight">
                            Escolha um modelo de capa
                        </h2>
                    </div>
                    <div className="grid w-full max-w-4xl grid-cols-2 gap-4 md:grid-cols-3">
                        {COVER_PICKER_KEYS.map((key) => (
                            <Button
                                key={key}
                                type="button"
                                variant="ghost"
                                data-testid={`cover-template-${key}`}
                                onClick={() => state.selectTemplate(key)}
                                className="flex h-auto flex-col gap-2 rounded-lg border border-border bg-surface p-2 hover:border-border-strong hover:bg-surface hover:shadow-md"
                            >
                                <CoverThumbnail
                                    width={220}
                                    className="mx-auto rounded-sm border border-border"
                                    cover={{ template_key: key }}
                                    context={fieldContext}
                                    customizations={customizations}
                                />
                                <span className="pb-1 text-center text-xs font-semibold">
                                    {templates[key].name}
                                </span>
                            </Button>
                        ))}
                    </div>
                </div>
            </CoverShell>
        );
    }

    const fieldRows: Array<{
        keys: CoverFieldKey[];
        label: string;
    }> = [
        // Os três primeiros nascem desligados (COVER_DEFAULT_VISIBLE_FIELDS):
        // a capa só mostra dados do paciente/finalidade se o psicólogo ligar.
        { keys: ['patient_name'], label: 'Nome do paciente' },
        { keys: ['solicitante'], label: 'Solicitante' },
        { keys: ['finalidade'], label: 'Finalidade' },
        { keys: ['psychologist_name', 'crp'], label: 'Psicóloga(o) e CRP' },
        { keys: ['city_date'], label: 'Cidade e data' },
    ];

    const isFieldVisible = (key: CoverFieldKey) =>
        cover.fields?.[key]?.visible ?? COVER_DEFAULT_VISIBLE_FIELDS.includes(key);

    const hasColorOverride = Boolean(cover.colors);
    const effectivePrimary =
        cover.colors?.primary || customizations?.colors?.primary || DEFAULT_PRIMARY_COLOR;
    const effectiveSecondary =
        cover.colors?.secondary || customizations?.colors?.secondary || DEFAULT_SECONDARY_COLOR;
    /** As cores da marca do psicólogo, oferecidas como os primeiros swatches. */
    const brandColors = [
        customizations?.colors?.primary || DEFAULT_PRIMARY_COLOR,
        customizations?.colors?.secondary || DEFAULT_SECONDARY_COLOR,
    ];

    /** A faixa de templates: os premium + a chave legada da capa atual, se houver. */
    const stripKeys = COVER_PICKER_KEYS.includes(cover.template_key)
        ? COVER_PICKER_KEYS
        : [cover.template_key, ...COVER_PICKER_KEYS];

    const usesBrandColors = templates[cover.template_key].usesBrandColors;

    const coverActions = isLocked ? (
        <div data-testid="cover-locked-cta" className="flex items-center gap-1.5 md:gap-3">
            {coverAccess.availableFromPlan ? (
                <FeatureUnavailableChip
                    availablePlan={coverAccess.availableFromPlan}
                    message={`Capas personalizadas estão disponíveis no plano ${coverAccess.availableFromPlan.name}.`}
                    side="bottom"
                />
            ) : null}
            <Button
                type="button"
                variant="outline"
                data-testid="cover-use-only-here"
                onClick={openPlansModal}
                className={HEADER_SECONDARY_CLASS}
            >
                <ResponsiveLabel long="Usar só neste laudo" short="Só neste laudo" />
            </Button>
            <Button
                type="button"
                data-testid="cover-save-default"
                onClick={openPlansModal}
                className={HEADER_PRIMARY_CLASS}
            >
                <ResponsiveLabel long="Salvar e usar em todos os laudos" short="Salvar capa" />
            </Button>
        </div>
    ) : state.isApplied && cover.cover_id ? (
        state.isDiverged ? (
            <Button
                type="button"
                data-testid="cover-promote"
                onClick={() => state.promote(cover)}
                disabled={state.isPromoting || state.isReadOnly}
                className={HEADER_PRIMARY_CLASS}
            >
                {state.isPromoting && <Spinner className="size-4 animate-spin" />}
                <ResponsiveLabel long="Usar em todos os laudos novos" short="Usar em todos" />
            </Button>
        ) : null
    ) : (
        <>
            <Button
                type="button"
                variant="outline"
                data-testid="cover-use-only-here"
                onClick={state.useOnlyOnThisReport}
                disabled={state.isReadOnly}
                className={HEADER_SECONDARY_CLASS}
            >
                <ResponsiveLabel long="Usar só neste laudo" short="Só neste laudo" />
            </Button>
            <Button
                type="button"
                data-testid="cover-save-default"
                onClick={() => state.saveAsDefault(cover)}
                disabled={state.isSavingDefault || state.isReadOnly}
                className={HEADER_PRIMARY_CLASS}
            >
                {state.isSavingDefault && <Spinner className="size-4 animate-spin" />}
                <ResponsiveLabel long="Salvar e usar em todos os laudos" short="Salvar capa" />
            </Button>
        </>
    );

    return (
        <CoverShell onBack={state.closeCoverView} patientName={patientName} actions={coverActions}>
            <div
                data-testid="cover-view-editor"
                className="flex min-h-0 min-w-0 flex-1 overflow-hidden"
            >
                <div className="flex w-[300px] flex-none flex-col gap-5 overflow-y-auto border-r border-border bg-surface p-4">
                    <div className="flex flex-col gap-2">
                        <span className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                            Modelo
                        </span>
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {stripKeys.map((key) => (
                                <Button
                                    key={key}
                                    type="button"
                                    variant="ghost"
                                    data-testid={`cover-template-strip-${key}`}
                                    title={templates[key].name}
                                    onClick={() => state.selectTemplate(key)}
                                    className={cn(
                                        'h-auto flex-none rounded-sm border border-border p-0 hover:bg-transparent',
                                        cover.template_key === key &&
                                            'outline outline-2 outline-offset-1 outline-info',
                                    )}
                                >
                                    <CoverThumbnail
                                        width={40}
                                        cover={{ ...cover, template_key: key }}
                                        context={fieldContext}
                                        customizations={customizations}
                                    />
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                            Logo
                        </span>

                        {state.hasLogo ? (
                            <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-muted p-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-12 w-16 flex-none items-center justify-center overflow-hidden rounded border border-border bg-white">
                                        {state.logoUrl ? (
                                            <img
                                                src={state.logoUrl}
                                                alt="Logo"
                                                className="size-full object-contain"
                                            />
                                        ) : null}
                                    </div>
                                    <label
                                        htmlFor="cover-logo-toggle"
                                        className="flex flex-1 cursor-pointer items-center justify-between gap-2"
                                    >
                                        <span className="text-sm font-medium">Mostrar na capa</span>
                                        <Switch
                                            id="cover-logo-toggle"
                                            data-testid="cover-logo-toggle"
                                            aria-label="Mostrar a logo na capa"
                                            checked={state.showLogo}
                                            onCheckedChange={state.setShowLogo}
                                            disabled={state.isReadOnly}
                                        />
                                    </label>
                                </div>
                                <div className="flex items-center gap-1 border-t border-border pt-2">
                                    <TooltipIconButton
                                        label="Trocar logo"
                                        data-testid="cover-logo-replace"
                                        onClick={() => logoInputRef.current?.click()}
                                        disabled={
                                            state.isReadOnly ||
                                            state.logoUploadState.status === 'uploading'
                                        }
                                    >
                                        <Refresh className="size-4" />
                                    </TooltipIconButton>
                                    <TooltipIconButton
                                        label="Remover logo"
                                        variant="ghostDestructive"
                                        data-testid="cover-logo-remove"
                                        onClick={handleRemoveLogo}
                                        disabled={state.isReadOnly}
                                    >
                                        <Trash className="size-4" />
                                    </TooltipIconButton>
                                </div>
                            </div>
                        ) : state.logoAccess.isUnavailableInPlan &&
                          state.logoAccess.availableFromPlan ? (
                            <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border-strong p-3">
                                <p className="text-xs text-muted-foreground">
                                    Adicione a sua logo para exibi-la na capa.
                                </p>
                                <FeatureUnavailableChip
                                    availablePlan={state.logoAccess.availableFromPlan}
                                />
                            </div>
                        ) : (
                            <FileDropzone
                                accept={IMAGE_UPLOAD_ACCEPT}
                                loading={state.logoUploadState.status === 'uploading'}
                                disabled={state.isReadOnly || state.logoAccess.shouldBlock}
                                showSelectButton={false}
                                title="Arraste a sua logo"
                                description="ou clique para enviar · PNG, JPG ou WebP"
                                className="rounded-lg p-4"
                                onFilesSelected={(files) => {
                                    if (files[0]) void state.uploadLogo(files[0]);
                                }}
                            />
                        )}

                        {state.logoUploadState.status === 'uploading' ? (
                            <div className="space-y-1">
                                <Progress
                                    value={state.logoUploadState.progress}
                                    className="h-1.5"
                                />
                                <p className="text-right text-[10px] text-muted-foreground">
                                    {state.logoUploadState.progress}%
                                </p>
                            </div>
                        ) : null}

                        {state.logoUploadState.status === 'error' ? (
                            <p className="flex items-center gap-1 text-[10px] text-destructive">
                                <CloseCircle className="size-3" /> {state.logoUploadState.error}
                            </p>
                        ) : null}

                        <Input
                            ref={logoInputRef}
                            type="file"
                            accept={IMAGE_UPLOAD_ACCEPT}
                            className="hidden"
                            data-testid="cover-logo-input"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const file = event.target.files?.[0];
                                if (file) void state.uploadLogo(file);
                                event.target.value = '';
                            }}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="flex items-center gap-1 text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                            Título
                            <TooltipIconButton
                                label="Sobre o título"
                                tooltip="Prescrito pela Resolução CFP 06/2019. Não editável."
                                side="top"
                                className="size-4 rounded-full text-muted-foreground hover:bg-transparent hover:text-foreground"
                            >
                                <InfoCircle className="size-3" />
                            </TooltipIconButton>
                        </span>
                        <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2">
                            <span className="text-sm font-semibold">Laudo Psicológico</span>
                            <span className="text-[0.5625rem] font-bold tracking-wide text-muted-foreground">
                                CFP §2º I
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <Label
                            htmlFor="cover-subtitle"
                            className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground"
                        >
                            Subtítulo
                        </Label>
                        <Input
                            id="cover-subtitle"
                            data-testid="cover-subtitle-input"
                            value={cover.fields?.subtitle?.text ?? 'Avaliação Psicológica'}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                state.setSubtitle(event.target.value)
                            }
                            disabled={state.isReadOnly}
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <Label
                            htmlFor="cover-font"
                            className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground"
                        >
                            Fonte
                        </Label>
                        <Select
                            value={cover.font ?? DEFAULT_FONT_VALUE}
                            onValueChange={(value: string) =>
                                state.setFont(value === DEFAULT_FONT_VALUE ? undefined : value)
                            }
                            disabled={state.isReadOnly}
                        >
                            <SelectTrigger id="cover-font" data-testid="cover-font-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={DEFAULT_FONT_VALUE}>Padrão do design</SelectItem>
                                {FONT_OPTIONS.map((font) => (
                                    <SelectItem
                                        key={font.value}
                                        value={font.value}
                                        style={{ fontFamily: font.value }}
                                    >
                                        {font.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <span className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                            Tamanho do título
                        </span>
                        <div className="flex gap-1 rounded-lg border border-border p-1">
                            {TITLE_SIZES.map((size) => (
                                <Button
                                    key={size.value}
                                    type="button"
                                    variant="ghost"
                                    title={size.title}
                                    data-testid={`cover-title-size-${size.value}`}
                                    onClick={() => state.setTitleSize(size.value)}
                                    disabled={state.isReadOnly}
                                    className={cn(
                                        'h-7 flex-1 rounded-md px-0 py-1 text-xs font-semibold',
                                        (cover.title_size ?? 'medium') === size.value
                                            ? 'hover:bg-primary/92 bg-primary text-primary-foreground hover:text-primary-foreground'
                                            : 'text-muted-foreground hover:bg-surface-muted',
                                    )}
                                >
                                    {size.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <span className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                            Campos
                        </span>
                        {fieldRows.map((row) => (
                            <div
                                key={row.keys[0]}
                                className="flex items-start gap-3 border-b border-border py-2 last:border-b-0"
                            >
                                <Checkbox
                                    id={`cover-field-${row.keys[0]}`}
                                    data-testid={`cover-field-${row.keys[0]}`}
                                    checked={isFieldVisible(row.keys[0])}
                                    onCheckedChange={(checked) =>
                                        state.setFieldVisible(row.keys, !!checked)
                                    }
                                    disabled={state.isReadOnly}
                                    className="mt-0.5"
                                />
                                <Label
                                    htmlFor={`cover-field-${row.keys[0]}`}
                                    className="min-w-0 flex-1 cursor-pointer truncate text-sm font-medium"
                                >
                                    {row.label}
                                </Label>
                            </div>
                        ))}
                    </div>

                    {usesBrandColors ? (
                        <div className="flex flex-col gap-2">
                            <span className="text-[0.625rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                                Cores
                            </span>
                            <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                                <div data-testid="cover-color-primary">
                                    <ColorSwatchPicker
                                        ariaLabel="Cor primária da capa"
                                        value={effectivePrimary}
                                        defaultColor={DEFAULT_PRIMARY_COLOR}
                                        brandColors={brandColors}
                                        onChange={(color) =>
                                            state.setColors({
                                                primary: color,
                                                secondary: effectiveSecondary,
                                            })
                                        }
                                        disabled={state.isReadOnly}
                                    />
                                </div>
                                <div data-testid="cover-color-secondary">
                                    <ColorSwatchPicker
                                        ariaLabel="Cor secundária da capa"
                                        value={effectiveSecondary}
                                        defaultColor={DEFAULT_SECONDARY_COLOR}
                                        brandColors={brandColors}
                                        onChange={(color) =>
                                            state.setColors({
                                                primary: effectivePrimary,
                                                secondary: color,
                                            })
                                        }
                                        disabled={state.isReadOnly}
                                    />
                                </div>
                                <p className="flex-1 text-xs text-muted-foreground">
                                    {hasColorOverride ? (
                                        <Button
                                            type="button"
                                            variant="link"
                                            data-testid="cover-colors-reset"
                                            onClick={() => state.setColors(undefined)}
                                            className="h-auto p-0 text-xs font-medium text-info"
                                        >
                                            Usar cores da marca
                                        </Button>
                                    ) : (
                                        <>
                                            Usando as cores primárias do{' '}
                                            <b className="font-semibold">seu laudo</b>
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div
                    data-testid="cover-stage"
                    className="flex flex-1 items-start justify-center overflow-auto bg-surface-muted p-8"
                >
                    <CoverThumbnail
                        width={480}
                        className="shadow-[0_1px_2px_rgba(15,23,42,0.14),0_10px_30px_rgba(15,23,42,0.13)]"
                        cover={cover}
                        context={fieldContext}
                        customizations={customizations}
                    />
                </div>
            </div>
        </CoverShell>
    );
};
